import {
  PropsWithChildren,
  useEffect,
  useMemo,
  type ReactNode
} from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { useSession } from "../auth/useSession";
import { Link, useLocation } from "react-router-dom";
import { FloatingChatWidget } from "./chat/FloatingChatWidget";
import { getUserDisplayLabel } from "../lib/userDisplay";
import {
  refreshPageAnimations,
  setPageShellReady
} from "../lib/animations";
import { useTranslation } from "react-i18next";

export function Layout({ children }: PropsWithChildren) {
  const { session, loading, error } = useSession();
  const location = useLocation();
  const { t } = useTranslation("common");

  const navbarConfig = useMemo<{
    showAuthButtons: boolean;
    rightSlot?: ReactNode;
  }>(() => {
    // If SessionProvider is missing (e.g. some extension renders this),
    // behave like a logged-out shell so we don't crash.
    if (error === "SessionProviderMissing") {
      return { showAuthButtons: true };
    }

    if (loading) {
      return { showAuthButtons: true };
    }

    if (session?.authenticated && session.user) {
      const label = getUserDisplayLabel(session.user);
      const rightSlot = (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="hidden text-xs font-medium text-[#6b7280] sm:inline">
            {label}
          </span>

          {/* Dashboard button */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-full border border-[#5919c1] px-4 py-2 text-xs font-semibold text-[#5919c1] transition hover:-translate-y-0.5 hover:bg-[#5919c1] hover:text-white"
          >
            <i className="fa-solid fa-gauge-high text-sm" />
            {/* default 'Dashboard' text if key missing */}
            <span>{t("nav.dashboard", "Dashboard")}</span>
          </Link>

          {/* Logout button */}
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[#d7c8ff] px-4 py-2 text-xs font-semibold text-[#5d2bbd] transition hover:-translate-y-0.5 hover:border-[#5d2bbd] hover:bg-[#f3ecff]"
            onClick={() => {
              window.location.href = "/auth/logout";
            }}
          >
            <i className="fa-solid fa-arrow-right-from-bracket text-sm" />
            {/* default 'Logout' text if key missing */}
            <span>{t("nav.logout", "Logout")}</span>
          </button>
        </div>
      );

      return { showAuthButtons: false, rightSlot };
    }

    // Not authenticated: show normal auth buttons
    return { showAuthButtons: true };
  }, [loading, session?.authenticated, session?.user, error, t]);

  // Initial shell + per-route animation setup
  useEffect(() => {
    setPageShellReady();

    if (typeof window === "undefined") return;
    const reduce =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const main = document.querySelector<HTMLElement>("main[data-animate]");
    if (!main) return;

    const childElements = Array.from(main.children) as HTMLElement[];
    childElements.forEach((el, index) => {
      if (!el.dataset.animate)
        el.dataset.animate = index === 0 ? "fade-in" : "fade-up";
      if (!el.style.getPropertyValue("--animate-delay")) {
        el.style.setProperty(
          "--animate-delay",
          `${Math.min(index * 60, 240)}ms`
        );
      }
    });
  }, [location.pathname]);

  // Refresh animations when route changes
  useEffect(() => {
    refreshPageAnimations();
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/x-icon" href="/static/favicon.ico" />
      </Helmet>

      {/* safe-area for iOS notches */}
      <div style={{ paddingTop: "env(safe-area-inset-top)" }} />

      <div className="flex min-h-screen flex-col bg-white text-[#0f0f0f]">
        <Navbar
          showAuthButtons={navbarConfig.showAuthButtons}
          rightSlot={navbarConfig.rightSlot}
        />

        <main className="flex-1" data-animate="fade-in">
          {children}
        </main>

        <Footer />
      </div>
      <FloatingChatWidget />
    </>
  );
}
